/*
 * Notes (to-dos, batch 5): the small sums the Notes view, the note editor
 * and the board share. Nothing here draws or asks the server anything, so
 * all of it can be read - and trusted - on its own.
 *
 * A note is a to-do with `kind: 'note'`: Google Keep's card rather than
 * Todoist's row. It has no tick of its own (the server answers 400 to
 * `done` on one); it has a body, or a CHECKLIST inside it, a colour, a pin,
 * photos and voice notes, and it can be archived out of the way.
 */

import { dayLabel, timeLabel } from '../../lib/when';

export const MAX_LINES = 100;         // the server's limit on a checklist
export const PREVIEW_LINES = 8;        // checklist lines a card shows

export const isNote = (t) => Boolean(t && t.kind === 'note');

/** A fresh id for a checklist line made here; the server may keep or replace it. */
export const lineId = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** "Show checkboxes": every non-empty line of the body becomes an unticked line. */
export const bodyToChecklist = (body) => String(body || '').split(/\r?\n/)
  .map((s) => s.trim()).filter(Boolean).slice(0, MAX_LINES)
  .map((text) => ({ id: lineId(), text, done: false }));

/** "Hide checkboxes": the lines back as a body, ticked ones included, in order. */
export const checklistToBody = (list) => (Array.isArray(list) ? list : [])
  .map((l) => String(l.text || '')).filter((s) => s.trim()).join('\n');

/** Unticked first, then ticked - each in its own order, as Keep folds them. */
export const splitLines = (list) => {
  const all = Array.isArray(list) ? list : [];
  return { open: all.filter((l) => !l.done), ticked: all.filter((l) => l.done) };
};

/**
 * What a card shows of a checklist: the unticked lines first, up to eight,
 * then "+3 more" for the rest of them and "2 ticked items" for the ticked.
 */
export function checklistPreview(list, max = PREVIEW_LINES) {
  const { open, ticked } = splitLines(list);
  const shown = open.slice(0, max);
  // Ticked lines are counted on a card, not drawn: what is left to do is
  // what a glance at the grid is for.
  return { shown, more: open.length - shown.length, ticked: ticked.length };
}

/** Does a note answer a search? Its title, its body and its lines. */
export function noteMatches(t, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const lines = Array.isArray(t.checklist) ? t.checklist.map((l) => l.text) : [];
  return [t.title, t.notes, ...lines].some((v) => v && String(v).toLowerCase().includes(q));
}

/** Pinned first (the most recently pinned on top), then the order they keep. */
export function pinnedFirst(a, b) {
  const pa = a.pinned ? 1 : 0;
  const pb = b.pinned ? 1 : 0;
  if (pa !== pb) return pb - pa;
  if (pa && pb) {
    const ta = Date.parse(a.pinned_at || '') || 0;
    const tb = Date.parse(b.pinned_at || '') || 0;
    if (ta !== tb) return tb - ta;
  }
  return (Number(a.position ?? 0) - Number(b.position ?? 0))
    || String(a.created_at || '').localeCompare(String(b.created_at || ''));
}

/** Newest-edited first, the way Keep's grid reads among the unpinned. */
export function byEdited(a, b) {
  const ta = Date.parse(a.updated_at || a.created_at || '') || 0;
  const tb = Date.parse(b.updated_at || b.created_at || '') || 0;
  return tb - ta;
}

/**
 * THE SERVER'S INDEX for a drop. What the screen shows and what the server
 * counts are not quite the same row of things: pinned to-dos float to the
 * top of a group on screen, and a list's notes sit among its to-dos on the
 * server but are not drawn in its List view. So a drop is described by its
 * neighbours instead - "just before this one", or failing that "just after
 * that one" - and that neighbour's place in the server's own order (by
 * position, everything in the section) is the index sent.
 *
 * `shown` are the siblings as drawn, WITHOUT the one moving; `index` where
 * it was dropped among them; `server` the same siblings as the server
 * orders them, also without it.
 */
export function serverIndex(shown, index, server) {
  const at = (id) => server.findIndex((t) => t.id === id);
  // Last on screen is last: after everything, notes included.
  if (index >= shown.length) return server.length;
  const next = shown[index];
  if (next && at(next.id) >= 0) return at(next.id);
  const prev = shown[index - 1];
  if (prev && at(prev.id) >= 0) return at(prev.id) + 1;
  return index === 0 ? 0 : server.length;
}

/**
 * "Edited 10:42" today, "Edited yesterday 15:00", "Edited Sun 18 Oct"
 * before - in the one en-GB, 24-hour voice of lib/when.js, whatever the
 * browser's own language is.
 */
export function editedLabel(when, now = new Date()) {
  const d = when ? new Date(when) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const day = dayLabel(d, now);
  if (day === 'Today') return `Edited ${timeLabel(d)}`;
  if (day === 'Yesterday') return `Edited yesterday ${timeLabel(d)}`;
  if (day === 'Tomorrow') return `Edited ${timeLabel(d)}`;   // a clock a little ahead of the server's
  return `Edited ${day}`;
}

// The server's `kind` decides: an SVG is stored as a 'file' (downloaded,
// never drawn inline), whatever its type says. Only without a kind is the
// type read - and an SVG still is not a picture.
const kindOf = (a) => a.kind || (/^image\/svg/.test(a.mime || '') ? 'file'
  : /^image\//.test(a.mime || '') ? 'image' : /^audio\//.test(a.mime || '') ? 'audio' : 'file');
export const imagesOf = (t) => (Array.isArray(t?.attachments) ? t.attachments : [])
  .filter((a) => a && kindOf(a) === 'image');
export const audioOf = (t) => (Array.isArray(t?.attachments) ? t.attachments : [])
  .filter((a) => a && kindOf(a) === 'audio');

/** The server's limits on attachments, checked before a file travels. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_FILES = 20;

/** "a", "a and b", "a, b and c". */
const andList = (xs) => (xs.length <= 1 ? xs.join('')
  : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const count = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * WHAT A CONVERSION KEPT, in the toast. Gordon: converting a note "loses all
 * the details, attachments, etc." - it no longer does (the server flips the
 * kind on the same row), and the toast says so rather than leaving him to
 * go and look: "Now a to-do, with its text, its checklist (3), 1 photo and
 * its label". A note that had no title says what it is called now. A to-do
 * made a note names what a note does not show - its date, priority and the
 * rest - and that it is kept for when it is a to-do again.
 *
 * `before` is the item as it was, `after` as the server answered, `kind`
 * what it became.
 */
export function convertedToast(before, after, kind) {
  const b = before || {};
  const a = after || b;
  const lines = Array.isArray(a.checklist) ? a.checklist.length : 0;
  const atts = Array.isArray(a.attachments) ? a.attachments : [];
  const photos = imagesOf(a).length;
  const voices = audioOf(a).length;
  const files = atts.length - photos - voices;
  const labels = Array.isArray(a.labels) ? a.labels.length : 0;
  const rems = Array.isArray(a.reminders) ? a.reminders.length : 0;
  const talk = Number(a.comment_count) || 0;
  const kept = [
    a.notes && String(a.notes).trim() && (kind === 'task' ? 'its text' : 'its notes'),
    lines && `its checklist (${lines})`,
    photos && count(photos, 'photo'),
    voices && count(voices, 'voice note'),
    files && count(files, 'file'),
    labels && (labels === 1 ? 'its label' : 'its labels'),
    rems && (rems === 1 ? 'its reminder' : 'its reminders'),
    a.color && 'its colour',
    talk && count(talk, 'comment')
  ].filter(Boolean);
  if (kind === 'task') {
    const named = !String(b.title || '').trim() && a.title ? ` called “${a.title}”` : '';
    return `Now a to-do${named}${kept.length ? `, with ${andList(kept)}` : ''}`;
  }
  const held = [
    a.due_date && 'date',
    Number(a.priority) > 0 && Number(a.priority) < 4 && 'priority',
    a.deadline && 'deadline',
    Number(a.duration_minutes) > 0 && 'length',
    a.repeat && 'repeat',
    a.assignee_key && a.assigned_by_key && a.assignee_key !== a.assigned_by_key && 'assignee',
    a.company_id && 'client',
    a.flagged && 'flag'
  ].filter(Boolean);
  const head = `Now a note${kept.length ? `, with ${andList(kept)}` : ''}`;
  return held.length ? `${head}. Its ${andList(held)} ${held.length === 1 ? 'is' : 'are'} kept for if it is a to-do again` : head;
}
